// Self-contained accordion controller for both etp-accordion (nestable) and
// etp-accordion-list. Replaces Bricks' native display-based bricksAccordion so
// collapsed items can use hidden="until-found" and stay findable by the browser.

export function initAccordion(scope) {
  const sc = scope || document;
  const accordions = new Set();
  if (sc.matches && sc.matches('.etp-accordion')) accordions.add(sc);
  sc.querySelectorAll('.etp-accordion').forEach((el) => accordions.add(el));

  accordions.forEach((accordion) => {
    if (accordion.dataset.etpAccordionReady) return;
    accordion.dataset.etpAccordionReady = 'true';

    const parsed = parseInt(accordion.dataset.transition || '200', 10);
    const duration = isNaN(parsed) ? 200 : parsed;
    const independent = (accordion.dataset.scriptArgs || '').includes('independentToggle');

    const getItems = () =>
      Array.from(accordion.children).filter((item) =>
        item.classList.contains('brxe-section') ||
        item.classList.contains('brxe-container') ||
        item.classList.contains('brxe-block') ||
        item.classList.contains('brxe-div') ||
        item.classList.contains('accordion-item')
      );

    const titleOf = (item) => item.querySelector('.accordion-title-wrapper');
    const contentOf = (item) => item.querySelector('.accordion-content-wrapper');

    const setState = (item, open) => {
      item.classList.toggle('brx-open', open);
      const title = titleOf(item);
      if (title) title.setAttribute('aria-expanded', open ? 'true' : 'false');
    };

    const clearAnim = (content) => {
      content.style.removeProperty('height');
      content.style.removeProperty('overflow');
      content.style.removeProperty('padding-top');
      content.style.removeProperty('padding-bottom');
      content.style.removeProperty('transition-property');
      content.style.removeProperty('transition-duration');
      content.style.removeProperty('display');
    };

    const openItem = (item, animate = true) => {
      const content = contentOf(item);
      if (!content) return;

      content.removeAttribute('hidden');
      setState(item, true);

      if (!animate) return;

      content.style.display = 'block';
      const target = content.offsetHeight;
      content.style.overflow = 'hidden';
      content.style.height = '0';
      content.style.paddingTop = '0';
      content.style.paddingBottom = '0';
      content.offsetHeight; // force reflow so the transition runs from 0
      content.style.transitionProperty = 'height, padding';
      content.style.transitionDuration = `${duration}ms`;
      content.style.height = `${target}px`;
      content.style.removeProperty('padding-top');
      content.style.removeProperty('padding-bottom');

      window.setTimeout(() => clearAnim(content), duration);
    };

    const closeItem = (item, animate = true) => {
      const content = contentOf(item);
      if (!content) return;

      if (!animate) {
        setState(item, false);
        clearAnim(content);
        content.setAttribute('hidden', 'until-found');
        return;
      }

      content.style.display = 'block';
      content.style.height = `${content.offsetHeight}px`;
      content.style.overflow = 'hidden';
      content.offsetHeight; // force reflow before collapsing
      content.style.transitionProperty = 'height, padding';
      content.style.transitionDuration = `${duration}ms`;
      content.style.height = '0';
      content.style.paddingTop = '0';
      content.style.paddingBottom = '0';

      setState(item, false);

      window.setTimeout(() => {
        clearAnim(content);
        content.setAttribute('hidden', 'until-found');
      }, duration);
    };

    const toggleItem = (item) => {
      const opening = !item.classList.contains('brx-open');

      if (opening && !independent) {
        getItems().forEach((other) => {
          if (other !== item && other.classList.contains('brx-open')) closeItem(other);
        });
      }

      if (opening) openItem(item);
      else closeItem(item);
    };

    // Initial state: server renders content closed; open items listed in
    // data-expand-item (comma separated, 0-based) or matching the URL hash.
    const expandIndexes = (accordion.dataset.expandItem || '')
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s !== '');
    const hash = window.location.hash || '';

    getItems().forEach((item, index) => {
      const content = contentOf(item);
      if (!content) return;

      const anchorId = item.id && !item.id.startsWith('brxe-') ? `#${item.id}` : '';
      const open = expandIndexes.includes(String(index)) || (!!anchorId && anchorId === hash);

      if (open) openItem(item, false);
      else closeItem(item, false);

      // Find-in-page or a text-fragment link matched collapsed content: the
      // browser already removed the hidden attribute, so just sync the state.
      content.addEventListener('beforematch', () => {
        clearAnim(content);
        setState(item, true);
      });
    });

    accordion.addEventListener('click', (e) => {
      const title = e.target.closest('.accordion-title-wrapper');
      if (!title || !accordion.contains(title)) return;

      const item = title.parentNode;
      if (!item || !contentOf(item)) return;

      e.stopPropagation(); // don't bubble into a parent accordion
      toggleItem(item);
    });

    const button = accordion.querySelector('.etp-accordion__expand-all');
    if (button) {
      button.dataset.expanded = 'false';
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const expand = button.dataset.expanded !== 'true';
        getItems().forEach((item) => (expand ? openItem(item, false) : closeItem(item, false)));
        button.dataset.expanded = expand ? 'true' : 'false';
        button.textContent = expand
          ? button.dataset.less || 'Collapse all'
          : button.dataset.more || 'Expand all';
      });
    }
  });
}
